import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { RefreshCw } from "lucide-react";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";
import { RATE_INPUT_STEP } from "@/utils/decimal";

const classifications = [
  { Code: "001", Description: "Breastfeeding equipment" },
  { Code: "002", Description: "Child care centres and kindergartens fees" },
  { Code: "022", Description: "Others" },
];

const taxTypes = [
  { Code: "01", Description: "Sales Tax" },
  { Code: "02", Description: "Service Tax" },
  { Code: "03", Description: "Tourism Tax" },
  { Code: "04", Description: "High-Value Goods Tax" },
  { Code: "05", Description: "Sales Tax on Low Value Goods" },
  { Code: "06", Description: "Not Applicable" },
  { Code: "E", Description: "Tax exemption (where applicable)" },
];

const initialValues = {
  classification: "022",
  taxType: "06",
  countryOfOrigin: "MYS",
};

const formSchema = z.object({
  id: z.string().optional(),
  classification: z.string({
    required_error: "Please select a classification",
  }),
  description: z
    .string()
    .min(1, "Description is required")
    .max(200, "Description must be less than 200 characters"),
  unitPrice: z
    .string()
    .min(1, "Unit price is required")
    .refine((val) => !Number.isNaN(Number(val)), "Must be a valid number")
    .refine((val) => Number(val) >= 0, "Price must be non-negative"),
  taxType: z.string({
    required_error: "Please select a tax type",
  }),
  taxRate: z
    .string()
    .refine((val) => !Number.isNaN(Number(val)), "Must be a valid number")
    .refine(
      (val) => Number(val) >= 0 && Number(val) <= 100,
      "Tax rate must be between 0 and 100"
    ),
  detailsOfTaxExemption: z.string().nullable(),
  productTariffCode: z.string().nullable(),
  countryOfOrigin: z
    .string()
    .min(3, "Country code must be 3 characters")
    .max(3, "Country code must be 3 characters")
    .regex(/^[A-Z]{3}$/, "Must be 3 uppercase letters"),
  itemCode: z
    .string()
    .min(1, "Item code is required")
    .regex(
      /^[A-Za-z0-9-_]+$/,
      "Only alphanumeric characters, hyphens, and underscores allowed"
    ),
});

export const ItemsFormPage = () => {
  const { step } = useDecimalPrecision();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    alert(data)
  };

  const resetForm = () => {
    // Reset all form fields including select fields
    for (const [key, value] of Object.entries(initialValues)) {
      form.setValue(key as keyof typeof initialValues, value);
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader className="border-b">
        <CardTitle className="text-2xl font-bold">Item Details</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {/* Left Column */}
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="classification"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Classification *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select classification" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {classifications.map((item) => (
                            <SelectItem key={item.Code} value={item.Code}>
                              {item.Description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormDescription>Maximum 200 characters</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="unitPrice"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit Price *</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" step={step} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="itemCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Item Code *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormDescription>
                        Alphanumeric characters, hyphens, and underscores only
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="taxType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tax Type *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select tax type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {taxTypes.map((item) => (
                            <SelectItem key={item.Code} value={item.Code}>
                              {item.Description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="taxRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tax Rate (%)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          max="100"
                          step={RATE_INPUT_STEP}
                        />
                      </FormControl>
                      <FormDescription>
                        Enter a value between 0 and 100
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="countryOfOrigin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country of Origin *</FormLabel>
                      <FormControl>
                        <Input {...field} maxLength={3} />
                      </FormControl>
                      <FormDescription>
                        3-letter country code (e.g., MYS)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="productTariffCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Product Tariff Code</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="flex gap-4 justify-end pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Reset
              </Button>
              <Button type="submit">Submit</Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
